import React from 'react';
import './SlashCommandMenu.css';

export const SLASH_COMMANDS = [
  {
    name: 'doc',
    template: '/doc ',
    placeholder: '<topic>',
    group: 'Create',
    description: 'Generate a structured markdown document artifact',
  },
  {
    name: 'diagram',
    template: '/diagram ',
    placeholder: '<description>',
    group: 'Create',
    description: 'Produce a Mermaid diagram artifact',
  },
  {
    name: 'pros-cons',
    template: '/pros-cons ',
    placeholder: '<topic>',
    group: 'Decide',
    description: 'Weigh tradeoffs as a two-column list',
  },
  {
    name: 'vote',
    template: '/vote ',
    placeholder: '<question>: option, option',
    group: 'Decide',
    description: 'Create an interactive poll the team votes on',
  },
  {
    name: 'shortlist',
    template: '/shortlist ',
    placeholder: '<title>',
    group: 'Decide',
    description: 'Curate 3–7 candidate options',
  },
  {
    name: 'action-items',
    template: '/action-items',
    placeholder: '',
    group: 'Execute',
    description: 'Extract todos from recent conversation',
  },
  {
    name: 'focus',
    template: '/focus ',
    placeholder: '<goal>',
    group: 'Execute',
    description: 'Refocus discussion around a stated goal',
  },
  {
    name: 'context',
    template: '/context ',
    placeholder: '<hint>',
    group: 'Execute',
    description: 'Reference a sibling thread via #thread-slug',
  },
  {
    name: 'search',
    template: '/search ',
    placeholder: '<query>',
    group: 'Create',
    description: 'Search the live web and summarize findings with sources',
  },
];

/**
 * Filter the command list by a fuzzy substring match against the command name
 * (and description, for forgiveness). Empty query returns all commands.
 */
export function filterSlashCommands(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return SLASH_COMMANDS;
  return SLASH_COMMANDS.filter(
    (c) => c.name.includes(q) || c.description.toLowerCase().includes(q),
  );
}

const GROUP_ORDER = ['Create', 'Decide', 'Execute'];

function SlashCommandMenu({ query, activeIndex, onSelect, onDismiss }) {
  const filtered = filterSlashCommands(query);
  if (filtered.length === 0) return null;

  // Group while preserving original order, so arrow-key index lines up.
  const grouped = GROUP_ORDER.map((group) => ({
    group,
    items: filtered.filter((c) => c.group === group),
  })).filter((g) => g.items.length > 0);

  let runningIndex = 0;
  return (
    <div className="slash-menu" role="listbox" aria-label="AI slash commands">
      <div className="slash-menu-header">
        <span>Slash commands</span>
        <button
          type="button"
          className="slash-menu-dismiss"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onDismiss}
          aria-label="Close slash commands"
        >
          esc
        </button>
      </div>
      {grouped.map((g) => (
        <div key={g.group} className="slash-menu-group">
          <div className="slash-menu-group-label">{g.group}</div>
          {g.items.map((cmd) => {
            const idx = runningIndex++;
            const isActive = idx === activeIndex;
            return (
              <button
                key={cmd.name}
                type="button"
                role="option"
                aria-selected={isActive}
                className={`slash-menu-item ${isActive ? 'active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(cmd)}
              >
                <span className="slash-menu-name">/{cmd.name}</span>
                {cmd.placeholder && (
                  <span className="slash-menu-placeholder">{cmd.placeholder}</span>
                )}
                <span className="slash-menu-desc">{cmd.description}</span>
              </button>
            );
          })}
        </div>
      ))}
      <div className="slash-menu-footer">
        <span>↑↓ navigate · ↵ select · esc dismiss</span>
      </div>
    </div>
  );
}

export default SlashCommandMenu;
